import {
  doc,
  getDoc,
  setDoc,
  updateDoc,
  serverTimestamp,
  Timestamp,
} from "firebase/firestore";
import { db, COLLECTIONS } from "../config/firebase";

export class NotificationPreferencesService {
  /**
   * One read of the raw preferences doc — the shared source for
   * isNotificationEnabled, computeEventNotificationEnabled and
   * isChatMutedConsolidated (all three read this same
   * user_notification_preferences/{userId} doc, just different fields on
   * it). A caller that needs more than one of those checks for the same
   * user (every per-recipient notification-fan-out check) should call this
   * once and pass the result to the matching computeXxx() below instead of
   * calling the async isXxx() methods separately — each of those does its
   * own independent getDoc, so checking enabled-then-muted for one user
   * used to cost two round trips instead of one.
   * @param {string} userId
   * @returns {Promise<{data: object|null, error: Error|null}>} data is the
   *   doc's plain fields, or null if the doc doesn't exist yet (every
   *   computeXxx() below treats null the same as "no preferences set").
   */
  static async getRawPreferences(userId) {
    try {
      const prefDoc = await getDoc(
        doc(db, COLLECTIONS.USER_NOTIFICATION_PREFERENCES, userId)
      );
      return { data: prefDoc.exists() ? prefDoc.data() : null, error: null };
    } catch (error) {
      if (__DEV__) console.error("Get raw preferences error:", error);
      return { data: null, error };
    }
  }

  /** Pure: same rule as isNotificationEnabled, given a getRawPreferences() result. */
  static computeNotificationEnabled(rawData, clubId) {
    const preferences = rawData?.club_preferences || {};
    return preferences[clubId] !== false; // Only false if explicitly set to false
  }

  /** Pure: per-event notification opt-out rule, given a getRawPreferences() result. */
  static computeEventNotificationEnabled(rawData, eventId) {
    const eventPreferences = rawData?.event_preferences || {};
    return eventPreferences[eventId] !== false; // Only false if explicitly set to false
  }

  /** Pure: same rule as isChatMutedConsolidated, given a getRawPreferences() result. */
  static computeChatMuted(rawData, chatType, chatId) {
    if (!rawData) return false;
    const chatSettingsKey = this.getChatSettingsKey(chatType, chatId);
    const chatSettings = rawData.chat_settings || {};
    const settings = chatSettings[chatSettingsKey];

    if (settings && Object.prototype.hasOwnProperty.call(settings, "muted")) {
      const isMuted = settings.muted === true;
      const mutedUntilMs =
        settings.muted_until && typeof settings.muted_until.toMillis === "function"
          ? settings.muted_until.toMillis()
          : null;
      const isExpired = isMuted && mutedUntilMs !== null && mutedUntilMs <= Date.now();
      return isMuted && !isExpired;
    }

    if (chatType === "club") {
      const chatMutePreferences = rawData.chat_mute_preferences || {};
      return chatMutePreferences[chatId] === true;
    }

    if (chatType === "event") {
      const eventPreferences = rawData.event_preferences || {};
      const enabled = eventPreferences[chatId] !== false; // default true
      return !enabled;
    }

    return false;
  }

  /**
   * Get user's notification preferences for all clubs
   * Returns an object with club_id as key and enabled (boolean) as value
   */
  static async getUserNotificationPreferences(userId) {
    try {
      const prefDocRef = doc(
        db,
        COLLECTIONS.USER_NOTIFICATION_PREFERENCES,
        userId
      );
      const prefDoc = await getDoc(prefDocRef);

      if (prefDoc.exists()) {
        const data = prefDoc.data();
        return { data: data.club_preferences || {}, error: null };
      } else {
        // Return empty object if no preferences exist (default: all enabled)
        return { data: {}, error: null };
      }
    } catch (error) {
      if (__DEV__) console.error("Get notification preferences error:", error);
      return { data: null, error };
    }
  }

  /**
   * Update notification preference for a specific club
   * @param {string} userId - User ID
   * @param {string} clubId - Club ID
   * @param {boolean} enabled - Whether notifications are enabled for this club
   */
  static async updateClubNotificationPreference(userId, clubId, enabled) {
    try {
      const prefDocRef = doc(
        db,
        COLLECTIONS.USER_NOTIFICATION_PREFERENCES,
        userId
      );
      const prefDoc = await getDoc(prefDocRef);

      if (prefDoc.exists()) {
        // Update existing document
        const currentData = prefDoc.data();
        const clubPreferences = currentData.club_preferences || {};
        clubPreferences[clubId] = enabled;

        await updateDoc(prefDocRef, {
          club_preferences: clubPreferences,
          updated_at: serverTimestamp(),
        });
      } else {
        // Create new document
        await setDoc(prefDocRef, {
          user_id: userId,
          club_preferences: {
            [clubId]: enabled,
          },
          created_at: serverTimestamp(),
          updated_at: serverTimestamp(),
        });
      }

      return { error: null };
    } catch (error) {
      if (__DEV__) console.error("Update notification preference error:", error);
      return { error };
    }
  }

  /**
   * Check if notifications are enabled for a specific club
   * Defaults to true if preference doesn't exist
   */
  static async isNotificationEnabled(userId, clubId) {
    try {
      const { data: rawData, error } = await this.getRawPreferences(userId);

      if (error) {
        // Default to enabled on error
        return { data: true, error };
      }

      return { data: this.computeNotificationEnabled(rawData, clubId), error: null };
    } catch (error) {
      if (__DEV__) console.error("Check notification enabled error:", error);
      return { data: true, error }; // Default to enabled on error
    }
  }

  /**
   * Get super_admin notification preferences
   * Returns an object with preference keys and boolean values
   */
  static async getSuperAdminNotificationPreferences(userId) {
    try {
      const prefDocRef = doc(
        db,
        COLLECTIONS.USER_NOTIFICATION_PREFERENCES,
        userId
      );
      const prefDoc = await getDoc(prefDocRef);

      if (prefDoc.exists()) {
        const data = prefDoc.data();
        return {
          data: {
            admin_requests: data.admin_requests !== false, // Default to true
            club_creation_requests: data.club_creation_requests !== false, // Default to true
          },
          error: null,
        };
      } else {
        // Return defaults (all enabled) if no preferences exist
        return {
          data: {
            admin_requests: true,
            club_creation_requests: true,
          },
          error: null,
        };
      }
    } catch (error) {
      if (__DEV__) console.error("Get super admin notification preferences error:", error);
      return { data: null, error };
    }
  }

  /**
   * Update super_admin notification preference
   * @param {string} userId - Super admin user ID
   * @param {string} preferenceType - 'admin_requests' or 'club_creation_requests'
   * @param {boolean} enabled - Whether notifications are enabled
   */
  static async updateSuperAdminNotificationPreference(
    userId,
    preferenceType,
    enabled
  ) {
    try {
      const prefDocRef = doc(
        db,
        COLLECTIONS.USER_NOTIFICATION_PREFERENCES,
        userId
      );
      const prefDoc = await getDoc(prefDocRef);

      if (prefDoc.exists()) {
        // Update existing document
        await updateDoc(prefDocRef, {
          [preferenceType]: enabled,
          updated_at: serverTimestamp(),
        });
      } else {
        // Create new document
        await setDoc(prefDocRef, {
          user_id: userId,
          admin_requests: preferenceType === "admin_requests" ? enabled : true,
          club_creation_requests:
            preferenceType === "club_creation_requests" ? enabled : true,
          created_at: serverTimestamp(),
          updated_at: serverTimestamp(),
        });
      }

      return { error: null };
    } catch (error) {
      if (__DEV__) console.error("Update super admin notification preference error:", error);
      return { error };
    }
  }

  /**
   * Check if super_admin has notifications enabled for admin requests
   */
  static async isAdminRequestNotificationEnabled(userId) {
    try {
      const { data: preferences, error } =
        await this.getSuperAdminNotificationPreferences(userId);

      if (error) {
        return { data: true, error }; // Default to enabled on error
      }

      return { data: preferences?.admin_requests !== false, error: null };
    } catch (error) {
      if (__DEV__) console.error("Check admin request notification enabled error:", error);
      return { data: true, error }; // Default to enabled on error
    }
  }

  /**
   * Check if super_admin has notifications enabled for club creation requests
   */
  static async isClubCreationRequestNotificationEnabled(userId) {
    try {
      const { data: preferences, error } =
        await this.getSuperAdminNotificationPreferences(userId);

      if (error) {
        return { data: true, error }; // Default to enabled on error
      }

      return {
        data: preferences?.club_creation_requests !== false,
        error: null,
      };
    } catch (error) {
      if (__DEV__) console.error(
        "Check club creation request notification enabled error:",
        error
      );
      return { data: true, error }; // Default to enabled on error
    }
  }

  // ─────────────────────────────────────────────────────────────────
  // Consolidated per-chat settings (chat_settings map)
  //
  // Data model (see docs/superpowers/specs/2026-07-22-customizable-chats-design.md):
  //   user_notification_preferences/{userId}.chat_settings = {
  //     "club_<clubId>":  { nickname, photo_url, color, muted, muted_until,
  //                         pinned, pinned_at, archived },
  //     "event_<eventId>": { same shape },
  //   }
  //
  // This is purely a data-model foundation: no screen wires these up yet.
  // The legacy `chat_mute_preferences[clubId]` and `event_preferences[eventId]`
  // fields are left untouched and are read-only fallbacks here — nothing in
  // this service writes to them anymore going forward.
  // ─────────────────────────────────────────────────────────────────

  /**
   * Build the composite key used inside `chat_settings`.
   * @param {"club"|"event"} chatType
   * @param {string} chatId - club_id or event_id
   */
  static getChatSettingsKey(chatType, chatId) {
    return `${chatType}_${chatId}`;
  }

  /**
   * Get the consolidated settings object for a single chat.
   * Returns `null` (not an error) if the user hasn't customized this chat yet.
   * @param {string} userId
   * @param {string} chatSettingsKey - result of getChatSettingsKey()
   */
  static async getChatSettings(userId, chatSettingsKey) {
    try {
      const prefDocRef = doc(
        db,
        COLLECTIONS.USER_NOTIFICATION_PREFERENCES,
        userId
      );
      const prefDoc = await getDoc(prefDocRef);

      if (prefDoc.exists()) {
        const data = prefDoc.data();
        const chatSettings = data.chat_settings || {};
        return { data: chatSettings[chatSettingsKey] || null, error: null };
      }

      return { data: null, error: null };
    } catch (error) {
      if (__DEV__) console.error("Get chat settings error:", error);
      return { data: null, error };
    }
  }

  /**
   * Merge a subset of fields into one chat's settings object, without
   * touching other chats' entries or other fields already set on this one.
   * Creates the parent doc/map if it doesn't exist yet.
   * @param {string} userId
   * @param {string} chatSettingsKey - result of getChatSettingsKey()
   * @param {object} fields - partial fields to merge, e.g. { nickname, color }
   *   or { muted: true, muted_until: null } or { pinned: true, pinned_at: serverTimestamp() }
   */
  static async updateChatSettings(userId, chatSettingsKey, fields) {
    try {
      const prefDocRef = doc(
        db,
        COLLECTIONS.USER_NOTIFICATION_PREFERENCES,
        userId
      );

      // setDoc with {merge:true} deep-merges nested map fields: this writes
      // only `fields` into chat_settings[chatSettingsKey], leaving every
      // other chat's entry and every other field already on this entry
      // untouched — and creates the doc if it doesn't exist yet. No
      // preceding getDoc needed, which removes the read-modify-write race
      // the previous version had (two near-simultaneous saves — two
      // devices, or a pin + a customization in quick succession — could
      // each read the same starting map and the second write would
      // silently clobber the first's change).
      await setDoc(
        prefDocRef,
        {
          user_id: userId,
          chat_settings: { [chatSettingsKey]: fields },
          updated_at: serverTimestamp(),
        },
        { merge: true }
      );

      return { error: null };
    } catch (error) {
      if (__DEV__) console.error("Update chat settings error:", error);
      return { error };
    }
  }

  /**
   * Consolidated "is this chat muted" check with a read-path fallback to the
   * legacy per-collection boolean fields for users not yet migrated to
   * `chat_settings`. Always returns true === muted, for both club and event
   * chats, even though the two legacy fields have inverted semantics
   * (`chat_mute_preferences` stores "muted" directly, `event_preferences`
   * stores "enabled" i.e. the opposite).
   *
   * Never writes to the legacy fields — callers that toggle mute going
   * forward should write only via updateChatSettings().
   *
   * @param {string} userId
   * @param {"club"|"event"} chatType
   * @param {string} chatId - club_id or event_id
   */
  static async isChatMutedConsolidated(userId, chatType, chatId) {
    try {
      const { data: rawData, error } = await this.getRawPreferences(userId);
      if (error) return { data: false, error };
      return { data: this.computeChatMuted(rawData, chatType, chatId), error: null };
    } catch (error) {
      if (__DEV__) console.error("Check consolidated chat mute error:", error);
      return { data: false, error };
    }
  }

  /**
   * Phase B (docs/superpowers/specs/2026-07-22-customizable-chats-design.md):
   * consolidated mute WRITE path — always writes chat_settings[key].muted /
   * muted_until directly, meaning muted=true always means muted for both
   * club and event chats. Replaces the two divergent write paths this
   * service used to expose:
   *   - `updateChatMutePreference` (club) wrote chat_mute_preferences[clubId]
   *   - `updateEventNotificationPreference` (event, called directly from
   *     EventChatScreen's old mute toggle) wrote event_preferences[eventId]
   *     using inverted "enabled" semantics
   * Both legacy setters had no remaining callers and were removed in cleanup
   * 2026-10-11 (the event_preferences "enabled" field is still READ as the
   * per-event opt-out by computeEventNotificationEnabled); ClubChatService.
   * toggleChatMute and EventChatScreen's mute menu both route through this
   * function now.
   * @param {string} userId
   * @param {"club"|"event"} chatType
   * @param {string} chatId - club_id or event_id
   * @param {boolean} muted
   * @param {"1h"|"8h"|"forever"|null} [duration] - only meaningful when
   *   muted is true; ignored when muted is false (unmuting always clears
   *   muted_until). "1h"/"8h" compute an expiry Timestamp; "forever" (or
   *   omitted while muted) leaves muted_until as null, meaning muted
   *   indefinitely until the user turns it back off.
   */
  static async setChatMuted(userId, chatType, chatId, muted, duration = null) {
    try {
      const chatSettingsKey = this.getChatSettingsKey(chatType, chatId);
      let mutedUntil = null;
      if (muted) {
        if (duration === "1h") {
          mutedUntil = Timestamp.fromMillis(Date.now() + 60 * 60 * 1000);
        } else if (duration === "8h") {
          mutedUntil = Timestamp.fromMillis(Date.now() + 8 * 60 * 60 * 1000);
        }
        // "forever" (or any other/omitted value) while muted => mutedUntil
        // stays null, meaning muted indefinitely.
      }

      return await this.updateChatSettings(userId, chatSettingsKey, {
        muted,
        muted_until: mutedUntil,
      });
    } catch (error) {
      if (__DEV__) console.error("Set chat muted error:", error);
      return { error };
    }
  }

  /**
   * Get the entire `chat_settings` map in one doc read — used by list views
   * (e.g. ChatsScreen's unified inbox) that need to overlay a nickname/photo/
   * color on many rows at once without one getChatSettings() read per row.
   * @param {string} userId
   * @returns {{data: object, error: null}} data is `{}` if unset.
   */
  static async getAllChatSettings(userId) {
    try {
      const prefDocRef = doc(
        db,
        COLLECTIONS.USER_NOTIFICATION_PREFERENCES,
        userId
      );
      const prefDoc = await getDoc(prefDocRef);

      if (prefDoc.exists()) {
        const data = prefDoc.data();
        return { data: data.chat_settings || {}, error: null };
      }

      return { data: {}, error: null };
    } catch (error) {
      if (__DEV__) console.error("Get all chat settings error:", error);
      return { data: {}, error };
    }
  }

  /**
   * Phase C (docs/superpowers/specs/2026-07-22-customizable-chats-design.md):
   * pin/unpin a chat in ChatsScreen's unified inbox. Pinning stamps
   * `pinned_at` (used to order multiple pinned chats, most-recently-pinned
   * first per Decision 3); unpinning clears it back to null. Purely a
   * personal inbox ordering flag on this user's own preferences doc — never
   * touches the club/event doc or any membership record.
   * @param {string} userId
   * @param {"club"|"event"} chatType
   * @param {string} chatId - club_id or event_id
   * @param {boolean} pinned
   */
  static async setChatPinned(userId, chatType, chatId, pinned) {
    try {
      const chatSettingsKey = this.getChatSettingsKey(chatType, chatId);
      return await this.updateChatSettings(userId, chatSettingsKey, {
        pinned,
        pinned_at: pinned ? serverTimestamp() : null,
      });
    } catch (error) {
      if (__DEV__) console.error("Set chat pinned error:", error);
      return { error };
    }
  }

  /**
   * Phase C: archive/unarchive a chat — hides it from ChatsScreen's default
   * unified inbox list (surfaced instead under a separate "Archived"
   * section the user opts into) without leaving the chat or affecting any
   * other member. Purely a personal inbox-visibility flag.
   * @param {string} userId
   * @param {"club"|"event"} chatType
   * @param {string} chatId - club_id or event_id
   * @param {boolean} archived
   */
  static async setChatArchived(userId, chatType, chatId, archived) {
    try {
      const chatSettingsKey = this.getChatSettingsKey(chatType, chatId);
      // Archiving also clears any pin — a chat can't be both hidden from
      // the default list and floated to the top of it. Without this,
      // unarchiving later would incorrectly land the chat straight back in
      // the Pinned section instead of its normal activity-sorted position.
      const fields = archived
        ? { archived, pinned: false, pinned_at: null }
        : { archived };
      return await this.updateChatSettings(userId, chatSettingsKey, fields);
    } catch (error) {
      if (__DEV__) console.error("Set chat archived error:", error);
      return { error };
    }
  }
}
